import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Button
} from "react-bootstrap";
import GridIcon from "../../../images/grid-icon.png";
import ListIcon from "../../../images/list-icon.svg";
import SearchIcon from "../../../images/search-icon.png";
import EditIcon from "../../../images/edit-icon.svg";
import DeleteIcon from "../../../images/delete-icon.svg";
import FilterIcon from "../../../images/filter-icon.svg";
import { Link } from "react-router-dom";
import { FiMoreVertical, FiChevronLeft, FiChevronRight } from "../../../icons";
import { Dropdown } from "react-bootstrap";
import UploadIcon from "../../../images/profile-dp.svg";
import NoContactsFoundImg from "../../../images/no-contacts-found.png";
import AddContactModal from "./AddContactModal.tsx";
import EditContactModal from "./EditContactModal.tsx";
import DeleteContactModal from "./DeleteContactModal.tsx";
import { contactApi, Contact } from "./contactApi.tsx";
import { toast } from "react-toastify";
import GiftLoader from '../../common/GiftLoader';
import "./MyContracts.scss";

type ViewMode = "grid" | "list";
type SortOrder = "asc" | "desc";

// Interface for display contact (formatted for UI)
interface DisplayContact {
  _id?: string;
  name: string;
  type: string;
  date: string;
  frequency: string;
  image: string;
  email: string;
  phone: string;
  alternatePhone: string;
  address: string;
  relationship: string;
  isPrimaryLegacyContact?: boolean;
  isSecondaryLegacyContact?: boolean;
}

// NoContactsFound Component
const NoContactsFound: React.FC<{ 
  hasSearchOrFilter?: boolean; 
  onClearSearch?: () => void; 
}> = ({ hasSearchOrFilter = false, onClearSearch }) => {
  const getNoContactsMessage = () => {
    if (hasSearchOrFilter) {
      return {
        title: "No contacts found",
        subtitle: "No contacts match your current search criteria",
        description: "Try adjusting your search terms or clear the filters to see all contacts."
      };
    }
    
    return {
      title: "No contacts yet",
      subtitle: "Your contact list is waiting to be started",
      description: "Add your first contact to begin building your network."
    };
  };

  const content = getNoContactsMessage();

  return (
    <motion.div
      className="no-contacts-found-container"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '3rem 1rem',
        minHeight: '400px'
      }}
    >
      {/* Animated Image */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          delay: 0.2,
          duration: 0.8,
          type: "spring",
          stiffness: 200,
          damping: 15
        }}
        style={{ marginBottom: '1rem' }}
      >
        <motion.img
          src={NoContactsFoundImg}
          alt="No contacts found"
          animate={{ 
            y: [0, -10, 0],
            rotate: [0, 2, 0, -2, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            width: '150px',
            height: 'auto',
            maxWidth: '100%',
            filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))'
          }}
        />
      </motion.div>

      {/* Content with staggered animations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        style={{ maxWidth: '500px' }}
      >
        <motion.h3
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#2c3e50',
            marginBottom: '0.5rem',
            lineHeight: '1.3'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          {content.title}
        </motion.h3>

        <motion.p
          style={{
            fontSize: '1.1rem',
            color: '#5a6c7d',
            marginBottom: '0.75rem',
            lineHeight: '1.4'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
        >
          {content.subtitle}
        </motion.p>

        <motion.p
          style={{
            fontSize: '0.95rem',
            color: '#7f8c8d',
            marginBottom: '2rem',
            lineHeight: '1.5'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.4 }}
        >
          {content.description}
        </motion.p>

        {/* Clear Filters Button */}
        {hasSearchOrFilter && onClearSearch && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 0.4 }}
          >
            <motion.button
              className="btn btn-outline-primary btn-lg"
              onClick={onClearSearch}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(57, 87, 160, 0.3)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ 
                scale: 0.95,
                transition: { duration: 0.1 }
              }}
              style={{
                borderRadius: '50px',
                padding: '12px 30px',
                fontWeight: '500',
                border: '2px solid #3957A0',
                color: '#3957A0',
                background: 'transparent',
                transition: 'all 0.3s ease'
              }}
            >
              🔍 Clear Search & Filters
            </motion.button>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Utility function to generate initials from contact name
const generateContactInitials = (name: string) => {
  if (!name) return 'CO'; // Default: Contact
  
  const words = name.trim().split(' ');
  if (words.length >= 2) {
    return `${words[0].charAt(0).toUpperCase()}${words[1].charAt(0).toUpperCase()}`;
  } else if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return 'CO';
};

// Contact Avatar Component with gradient initials fallback
const ContactAvatar: React.FC<{
  contact: DisplayContact;
  size?: 'small' | 'large';
  className?: string;
  onImageError?: () => void;
}> = ({ contact, size = 'large', className = '', onImageError }) => {
  const [imageError, setImageError] = useState(false);
  
  const handleImageError = () => {
    setImageError(true);
    if (onImageError) onImageError();
  };
  
  // Check if image is available and not the default upload icon
  const hasValidImage = contact.image && contact.image !== UploadIcon && !imageError;
  
  // Show image if available and no error
  if (hasValidImage) {
    return (
      <img
        src={contact.image}
        alt={contact.name}
        className={className}
        style={{
          width: size === 'small' ? '35px' : '130px',
          height: size === 'small' ? '35px' : '130px',
          objectFit: 'cover',
          borderRadius: size === 'small' ? '50%' : '26px',
        }}
        onError={handleImageError}
      />
    );
  }
  
  // Fallback to gradient initials
  const initials = generateContactInitials(contact.name);
  return (
    <div
      className={`contact-initials-avatar ${className}`}
      style={{
        width: size === 'small' ? '35px' : '130px',
        height: size === 'small' ? '35px' : '130px',
        background: 'linear-gradient(135deg, #3957A0 0%, #D7A360 50%, #99CAEB 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: size === 'small' ? '50%' : '26px',
        fontSize: size === 'small' ? '14px' : '36px',
        fontWeight: 'bold',
        margin: '0 auto',
      }}
    >
      {initials}
    </div>
  );
};

interface PaginationInfo {
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

interface ContactFilters {
  search: string;
  relationship: string;
  sortBy: string;
  sortOrder: SortOrder;
  page: number;
  limit: number;
}


const MyContracts: React.FC = () => {
  // Modal states
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // Data states
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  });

  // UI states
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  // Individual dropdown state for each contact
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Filter and search states with proper typing
  const [filters, setFilters] = useState<ContactFilters>({
    search: "",
    relationship: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    page: 1,
    limit: 10,
  });

  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    if (debouncedSearch !== filters.search) return;
    
    setFilters(prev => ({
      ...prev,
      page: 1
    }));
  }, [debouncedSearch, filters.relationship]);

  // Phone number formatting function
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Updated format address function - displays address1, address2 on one line and city, state, zip on another line
 const formatAddress = (contact: Contact): string => {
  const lines: string[] = [];
  
  // First line: address1 and address2 (on same line, separated by comma)
  const addressLine1Parts: string[] = [];
  if (contact.address1?.trim()) addressLine1Parts.push(contact.address1.trim());
  if (contact.address2?.trim()) addressLine1Parts.push(contact.address2.trim());
  if (addressLine1Parts.length > 0) {
    lines.push(addressLine1Parts.join(", "));
  }
  
  // Second line: city, state, zip (on same line, separated by comma)
  const addressLine2Parts: string[] = [];
  if (contact.city?.trim()) addressLine2Parts.push(contact.city.trim());
  if (contact.state?.trim()) addressLine2Parts.push(contact.state.trim());
  if (contact.zip?.trim()) addressLine2Parts.push(contact.zip.trim());
  if (addressLine2Parts.length > 0) {
    lines.push(addressLine2Parts.join(", "));
  }
  
  return lines.join("\n");
};

  // Get contact image with error handling
  const getContactImage = (contact: Contact): string => {
    if (imageErrors.has(contact._id || "")) {
      return UploadIcon;
    }

    if (contact.profileUrl && contact.profileUrl.trim() !== "") {
      return contact.profileUrl;
    }

    return UploadIcon;
  };

  const handleImageError = (contactId: string) => {
    setImageErrors((prev) => new Set(prev).add(contactId));
  };

  // Fetch contacts from API with all filters
  const fetchContacts = useCallback(async (filterParams: ContactFilters) => {
    try {
      setLoading(true);
      
      const params = {
        search: filterParams.search,
        page: filterParams.page,
        limit: filterParams.limit,
        sortBy: filterParams.sortBy,
        sortOrder: filterParams.sortOrder,
      };

      console.log('Fetching contacts with params:', params);

      const response = await contactApi.getAllContacts(params);

      if (response.status === "Success" && response.data) {
        // Capitalize first letter of name and relationship
        const capitalizeFirstLetter = (str: string) =>
          str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

        const formattedContacts = response.data.map(contact => ({
          ...contact,
          name: capitalizeFirstLetter(contact.name || ""),
          relationship: capitalizeFirstLetter(contact.relationship || "")
        }));

        setContacts(formattedContacts);

        // Update pagination info
        if (response.pagination) {
          setPagination({
            totalCount: response.pagination.totalCount || 0,
            totalPages: response.pagination.totalPages || 0,
            currentPage: response.pagination.currentPage || 1,
          });
        }
      } else {
        toast.error(response.message || "Failed to fetch contacts");
        setContacts([]);
        setPagination({ totalCount: 0, totalPages: 0, currentPage: 1 });
      }
    } catch (error: any) {
      console.error("Error fetching contacts:", error);
      toast.error(error.message || "Failed to fetch contacts");
      setContacts([]);
      setPagination({ totalCount: 0, totalPages: 0, currentPage: 1 });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load contacts when filters change
  useEffect(() => {
    const finalFilters = { 
      ...filters, 
      search: debouncedSearch 
    };
    fetchContacts(finalFilters);
  }, [fetchContacts, filters.page, filters.limit, filters.sortBy, filters.sortOrder, filters.relationship, debouncedSearch]);

  // Filter contacts locally by relationship (if needed for immediate UI feedback)
  const filteredContacts = contacts.filter((contact) => {
    if (!filters.relationship) return true;
    return contact.relationship
      .toLowerCase()
      .includes(filters.relationship.toLowerCase());
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId) {
        const currentDropdown = dropdownRefs.current[openDropdownId];
        if (
          currentDropdown &&
          !currentDropdown.contains(event.target as Node)
        ) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  // Handle add contact
  const handleAddContact = (newContact: Contact): void => {
    // Refresh the current page to include the new contact
    fetchContacts({ ...filters, search: debouncedSearch });
  };

  // Handle edit contact
  const handleOpenEditModal = (contact: Contact): void => {
    setEditingContact(contact);
    setShowEditModal(true);
    setOpenDropdownId(null);
  };

  const handleUpdateContact = (updatedContact: Contact): void => {
    setContacts((prevContacts) =>
      prevContacts.map((contact) =>
        contact._id === updatedContact._id ? updatedContact : contact
      )
    );
    setShowEditModal(false);
    setEditingContact(null);
  };

  // Handle delete contact
  const handleOpenDeleteModal = (contact: Contact): void => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
    setOpenDropdownId(null);
  };

  const handleConfirmDelete = async (): Promise<void> => {
  if (!selectedContact?._id) return;

  try {
    setDeleteLoading(true);

    const response = await contactApi.deleteContact(selectedContact._id);

    if (response.status === "Success") {
      // Refresh the contacts list
      fetchContacts({ ...filters, search: debouncedSearch });
      toast.success(response.message || "Contact deleted successfully!");
      handleCloseDeleteModal();
    } else {
      toast.error(response.message || "Failed to delete contact");
    }
  } catch (error: any) {
    console.error("Error deleting contact:", error);
    toast.error(error.message || "Failed to delete contact");
  } finally {
    setDeleteLoading(false);
  }
};

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false);
    setSelectedContact(null);
  };

  // Handle set legacy contact
  const handleSetLegacyContact = async (
    contactId: string,
    type: "primary" | "secondary"
  ): Promise<void> => {
    try {
      const response = await contactApi.setLegacyContact(contactId, type);

      if (response.status === "Success") {
        // Update contacts state immediately for better UX
        setContacts((prevContacts) =>
          prevContacts.map((contact) => {
            if (contact._id === contactId) {
              return {
                ...contact,
                isPrimaryLegacyContact: type === "primary",
                isSecondaryLegacyContact: type === "secondary",
              };
            } else {
              if (type === "primary") {
                return {
                  ...contact,
                  isPrimaryLegacyContact: false,
                };
              } else if (type === "secondary") {
                return {
                  ...contact,
                  isSecondaryLegacyContact: false,
                };
              }
              return contact;
            }
          })
        );

        toast.success(
          response.message ||
            `Contact set as ${type} legacy contact successfully!`
        );
        setOpenDropdownId(null);

        // Refresh from server after a short delay
        setTimeout(() => {
          fetchContacts({ ...filters, search: debouncedSearch });
        }, 1000);
      } else {
        toast.error(response.message || "Failed to set legacy contact");
      }
    } catch (error: any) {
      console.error("Error setting legacy contact:", error);
      toast.error(error.message || "Failed to set legacy contact");
    }
  };

  const handleCloseEditModal = (): void => {
    setShowEditModal(false);
    setEditingContact(null);
  };

  // Handle filter changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFilters(prev => ({
      ...prev,
      search: e.target.value
    }));
  }

  const handleSortChange = (sortBy: string): void => {
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
      page: 1 // Reset to first page when sort changes
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage: number): void => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  // Toggle dropdown for specific contact
  const toggleDropdown = (contactId: string): void => {
    setOpenDropdownId(openDropdownId === contactId ? null : contactId);
  };

  // Format contact data for display
  const formatContactForDisplay = (contact: Contact) => ({
    _id: contact._id,
    name: contact.name,
    type: contact.relationship,
    date:
      formatPhoneNumber(contact.phoneNumber) +
      (contact.alternatePhoneNumber
        ? ", " + formatPhoneNumber(contact.alternatePhoneNumber)
        : ""),
    frequency: formatAddress(contact),
    image: getContactImage(contact),
    email: contact.email,
    phone: formatPhoneNumber(contact.phoneNumber),
    alternatePhone: contact.alternatePhoneNumber
      ? formatPhoneNumber(contact.alternatePhoneNumber)
      : "",
    address: formatAddress(contact),
    relationship: contact.relationship,
    isPrimaryLegacyContact: contact.isPrimaryLegacyContact,
    isSecondaryLegacyContact: contact.isSecondaryLegacyContact,
  });

  const displayContacts = filteredContacts.map(formatContactForDisplay);

  // Pagination component
const PaginationComponent = () => {
  if (pagination.totalPages <= 1) return null;

  const pages: number[] = []; // Add proper typing here
  const maxVisiblePages = 5;
  let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="pagination-wrapper d-flex justify-content-between align-items-center mt-4">
      <div className="pagination-info">
        <span className="text-muted">
          Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{" "}
          {Math.min(pagination.currentPage * filters.limit, pagination.totalCount)} of{" "}
          {pagination.totalCount} contacts
        </span>
      </div>
      
      <div className="pagination-controls d-flex align-items-center gap-2">
        <Button
          variant="outline-primary"
          size="sm"
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(pagination.currentPage - 1)}
        >
          <FiChevronLeft />
        </Button>

        {startPage > 1 && (
          <>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handlePageChange(1)}
            >
              1
            </Button>
            {startPage > 2 && <span className="px-2">...</span>}
          </>
        )}

        {pages.map((page) => (
          <Button
            key={page}
            variant={page === pagination.currentPage ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => handlePageChange(page)}
          >
            {page}
          </Button>
        ))}

        {endPage < pagination.totalPages && (
          <>
            {endPage < pagination.totalPages - 1 && <span className="px-2">...</span>}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handlePageChange(pagination.totalPages)}
            >
              {pagination.totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline-primary"
          size="sm"
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.currentPage + 1)}
        >
          <FiChevronRight />
        </Button>
      </div>
    </div>
  );
};

  return (
    <>
      <section className="main-layout">
        <div className="gifts-body h-100 py">
          <div className="">
            <div className="gifts-body-in py-0">
              <section className="top-strip">
                <div className="container h-100">
                  <div className="text-end">
                    <aside className="top-strip-right">
                      <Button
                        className="bttn w-auto"
                        onClick={() => setShowModal(true)}
                      >
                        Add New Contact
                      </Button>
                    </aside>
                  </div>
                </div>
              </section>

              <div className="container bg-white member-area">
                <div className="profile-sec">
                  <div className="profile-filter">
                    <div className="d-flex justify-content-between">
                      <aside className="search">
                        <div className="search-box">
                          <input
                            type="text"
                            placeholder="Search contacts..."
                            value={filters.search}
                            onChange={handleSearchChange}
                          />
                          <span className="icon">
                            <img src={SearchIcon} alt="Search" />
                          </span>
                        </div>
                      </aside>
                      <aside className="filter">
                        <div className="filter-grid">
                          <Link
                            to="#"
                            className={viewMode === "grid" ? "active" : ""}
                            onClick={(e) => {
                              e.preventDefault();
                              setViewMode("grid");
                              setFilters(prev => ({ ...prev, limit: 6 }));
                            }}
                          >
                            <img src={GridIcon} alt="Grid View" />
                          </Link>
                          <Link
                            to="#"
                            className={viewMode === "list" ? "active" : ""}
                            onClick={(e) => {
                              e.preventDefault();
                              setViewMode("list");
                              setFilters(prev => ({ ...prev, limit: 10 }));
                            }}
                          >
                            <img src={ListIcon} alt="List View" />
                          </Link>
                        </div>
                      </aside>

                      {/* Mobile Filter Dropdown */}
                      <Dropdown
                        align="end"
                        className="filter-dropdown d-block d-lg-none"
                      >
                        <Dropdown.Toggle
                          as="aside"
                          className="filter-mbl border-0 bg-transparent p-0"
                          id="dropdown-filter"
                        >
                          <figure className="mb-0">
                            <img src={FilterIcon} alt="Filter" />
                          </figure>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="p-3" style={{ minWidth: "180px" }}>
                          <div className="d-flex gap-2">
                            <Dropdown.Item
                              as="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setViewMode("grid");
                                setFilters(prev => ({ ...prev, limit: 6 }));
                              }}
                              className={`d-flex align-items-center justify-content-center gap-2 view-mode-btn ${
                                viewMode === "grid" ? "active" : ""
                              }`}
                            >
                              <img src={GridIcon} alt="Grid View" />
                              <span>Grid</span>
                            </Dropdown.Item>
                            <Dropdown.Item
                              as="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setViewMode("list");
                                setFilters(prev => ({ ...prev, limit: 10 }));
                              }}
                              className={`d-flex align-items-center justify-content-center gap-2 view-mode-btn ${
                                viewMode === "list" ? "active" : ""
                              }`}
                            >
                              <img src={ListIcon} alt="List View" />
                              <span>List</span>
                            </Dropdown.Item>
                          </div>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>

                <section className="members-grid-area">
                  {loading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "400px" }}
                    >
                      <GiftLoader 
                        text="Loading your precious contacts..."
                        size="medium"
                        showText={true}
                      />
                    </div>
                  ) : displayContacts.length === 0 ? (
                    <NoContactsFound
                      hasSearchOrFilter={!!(filters.search || filters.relationship)}
                      onClearSearch={() => {
                        setFilters(prev => ({
                          ...prev,
                          search: "",
                          relationship: "",
                          page: 1
                        }));
                      }}
                    />
                  ) : viewMode === "grid" ? (
                    <div className="row">
                      {displayContacts.map((contact, index) => (
                        <motion.div
                          className="contact-grid col-xl-6 col-lg-6 col-md-6"
                          key={contact._id || index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ 
                            duration: 0.5, 
                            delay: index * 0.1,
                            ease: "easeOut"
                          }}
                          whileHover={{ 
                            y: -8,
                            transition: { duration: 0.3 }
                          }}
                        >
                          <motion.div 
                            className="contact-grid-in contact-card-interactive gift-card-interactive"
                            whileHover={{
                              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                              transition: { duration: 0.3 }
                            }}
                            style={{
                              position: 'relative',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {/* Galaxy border effect */}
                            <div className="contact-border-animation card-border-animation" />
                            <div className="table-wrapper">
                              <aside className="contact-grid-left">
                                <motion.figure
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <ContactAvatar
                                    contact={contact}
                                    size="large"
                                    onImageError={() => handleImageError(contact._id || "")}
                                  />
                                </motion.figure>
                              </aside>
                              <aside className="contact-grid-right">
                                {contact.isPrimaryLegacyContact && (
                                  <div className="tag">
                                    Primary Legacy Contact
                                  </div>
                                )}
                                {!contact.isPrimaryLegacyContact &&
                                  contact.isSecondaryLegacyContact && (
                                    <div className="tag">
                                      Secondary Legacy Contact
                                    </div>
                                  )}
                                <h2>{contact.name}</h2>
                                <span>{contact.type}</span>
                                <p>{contact.date}</p>
                                <p>
                                  <b>{contact.email}</b>
                                </p>
                                <p style={{ whiteSpace: 'pre-line' }}>{contact.frequency}</p>

                                <div
                                  className="member-edit"
                                  ref={(el) => {
                                    if (el && contact._id) {
                                      dropdownRefs.current[contact._id] = el;
                                    }
                                  }}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent card click
                                      toggleDropdown(contact._id || "");
                                    }}
                                    className="edit-toggle"
                                  >
                                    <FiMoreVertical size={18} />
                                  </button>
                                  <AnimatePresence>
                                    {openDropdownId === contact._id && (
                                      <motion.div
                                        className="modern-dropdown"
                                        initial={{ 
                                          opacity: 0, 
                                          scale: 0.95,
                                          y: -10
                                        }}
                                        animate={{ 
                                          opacity: 1, 
                                          scale: 1,
                                          y: 0
                                        }}
                                        exit={{ 
                                          opacity: 0, 
                                          scale: 0.95,
                                          y: -10
                                        }}
                                        transition={{ 
                                          duration: 0.2,
                                          ease: [0.25, 0.46, 0.45, 0.94]
                                        }}
                                      >
                                        <motion.div
                                          className="modern-dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const originalContact = contacts.find(
                                              (c) => c._id === contact._id
                                            );
                                            if (originalContact)
                                              handleOpenEditModal(originalContact);
                                          }}
                                          whileTap={{ scale: 0.98 }}
                                                                                  >
                                          {/* <span className="icon">✏️</span> */}
                                          Edit
                                        </motion.div>

                                        {!contact.isPrimaryLegacyContact && (
                                          <motion.div
                                            className="modern-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSetLegacyContact(
                                                contact._id || "",
                                                "primary"
                                              );
                                            }}
                                            whileHover={{ 
                                              backgroundColor: 'rgba(57, 87, 160, 0.08)',
                                              x: 2
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            transition={{ duration: 0.15 }}
                                            style={{
                                              padding: '6px 12px',
                                              borderRadius: '6px',
                                              cursor: 'pointer',
                                              fontSize: '13px',
                                              fontWeight: '500',
                                              color: '#374151',
                                              border: 'none',
                                              background: 'transparent',
                                              width: '100%',
                                              textAlign: 'left',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '6px'
                                            }}
                                          >
                                            {/* <span className="icon">⭐</span> */}
                                            Make Primary Legacy
                                          </motion.div>
                                        )}

                                        {!contact.isSecondaryLegacyContact && (
                                          <motion.div
                                            className="modern-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSetLegacyContact(
                                                contact._id || "",
                                                "secondary"
                                              );
                                            }}
                                            whileHover={{ 
                                              backgroundColor: 'rgba(57, 87, 160, 0.08)',
                                              x: 2
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            transition={{ duration: 0.15 }}
                                            style={{
                                              padding: '6px 12px',
                                              borderRadius: '6px',
                                              cursor: 'pointer',
                                              fontSize: '13px',
                                              fontWeight: '500',
                                              color: '#374151',
                                              border: 'none',
                                              background: 'transparent',
                                              width: '100%',
                                              textAlign: 'left',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '6px'
                                            }}
                                          >
                                            {/* <span className="icon">🌟</span> */}
                                            Make Secondary Legacy
                                          </motion.div>
                                        )}

                                        <motion.div
                                          className="modern-dropdown-item delete-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const originalContact = contacts.find(
                                              (c) => c._id === contact._id
                                            );
                                            if (originalContact)
                                              handleOpenDeleteModal(originalContact);
                                          }}
                                          whileTap={{ scale: 0.98 }}
                                        >
                                          {/* <span className="icon">🗑️</span> */}
                                          Delete
                                        </motion.div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              </aside>
                            </div>
                          </motion.div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle text-nowrap">
                        <thead style={{ backgroundColor: "#E7ECF1" }}>
                          <tr>
                            <th>Image</th>
                            <th 
                              style={{ cursor: "pointer" }}
                              onClick={() => handleSortChange("name")}
                            >
                              Name {filters.sortBy === "name" && (filters.sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th 
                              style={{ cursor: "pointer" }}
                              onClick={() => handleSortChange("relationship")}
                            >
                              Relationship {filters.sortBy === "relationship" && (filters.sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th 
                              style={{ cursor: "pointer" }}
                              onClick={() => handleSortChange("email")}
                            >
                              Email {filters.sortBy === "email" && (filters.sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th>Phone Number</th>
                            <th>Alternate Phone</th>
                            <th>Address</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayContacts.map((contact, index) => (
                            <motion.tr
                              key={contact._id || index}
                              className={openDropdownId === contact._id ? 'dropdown-active' : ''}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                duration: 0.3,
                                delay: index * 0.05
                              }}
                              whileHover={{
                                backgroundColor: "#f8f9fa",
                                transition: { duration: 0.2 }
                              }}
                            >
                              <td>
                                <div className="table-profile">
                                  <ContactAvatar
                                    contact={contact}
                                    size="small"
                                    onImageError={() => handleImageError(contact._id || "")}
                                  />
                                </div>
                              </td>
                              <td>{contact.name || "--"}</td>
                              <td>{contact.relationship || "--"}</td>
                              <td>{contact.email || "--"}</td>
                              <td>{contact.phone || "--"}</td>
                              <td>{contact.alternatePhone || "--"}</td>
                              <td style={{ whiteSpace: 'pre-line' }}>{contact.address || "--"}</td>
                              <td className="table-actions">
                                <div
                                  className="member-edit table-dropdown"
                                  ref={(el) => {
                                    if (el && contact._id) {
                                      dropdownRefs.current[contact._id] = el;
                                    }
                                  }}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDropdown(contact._id || "");
                                    }}
                                    className="edit-toggle"
                                  >
                                    <FiMoreVertical size={18} />
                                  </button>
                                  <AnimatePresence>
                                    {openDropdownId === contact._id && (
                                      <motion.div
                                        className="modern-dropdown"
                                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                        transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                                      >
                                        <motion.div
                                          className="modern-dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const originalContact = contacts.find(
                                              (c) => c._id === contact._id
                                            );
                                            if (originalContact)
                                              handleOpenEditModal(originalContact);
                                          }}
                                          whileTap={{ scale: 0.98 }}
                                        >
                                          Edit
                                        </motion.div>

                                        {!contact.isPrimaryLegacyContact && (
                                          <motion.div
                                            className="modern-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSetLegacyContact(
                                                contact._id || "",
                                                "primary"
                                              );
                                            }}
                                            whileHover={{
                                              backgroundColor: 'rgba(57, 87, 160, 0.08)',
                                              x: 2
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            transition={{ duration: 0.15 }}
                                          >
                                            Set as Primary Legacy
                                          </motion.div>
                                        )}

                                        {!contact.isSecondaryLegacyContact && (
                                          <motion.div
                                            className="modern-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSetLegacyContact(
                                                contact._id || "",
                                                "secondary"
                                              );
                                            }}
                                            whileHover={{
                                              backgroundColor: 'rgba(57, 87, 160, 0.08)',
                                              x: 2
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            transition={{ duration: 0.15 }}
                                          >
                                            Set as Secondary Legacy
                                          </motion.div>
                                        )}

                                        <motion.div
                                          className="modern-dropdown-item delete-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const originalContact = contacts.find(
                                              (c) => c._id === contact._id
                                            );
                                            if (originalContact)
                                              handleOpenDeleteModal(originalContact);
                                          }}
                                          whileTap={{ scale: 0.98 }}
                                        >
                                          Delete
                                        </motion.div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination Component */}
                  <PaginationComponent />
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add Contact Modal */}
      <AddContactModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onAdd={handleAddContact}
      />

      {/* Edit Contact Modal */}
      <EditContactModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        onUpdate={handleUpdateContact}
        editingContact={editingContact}
      />

      {/* Delete Modal */}
      <DeleteContactModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        selectedContact={selectedContact}
        loading={deleteLoading}
      />
    </>
  );
};

export default MyContracts;